import { BarChartOption, colors, LineChartOption } from '@creditsights/styleguide-web';
import { ECharts } from 'echarts/core';
import {
  DeteriorationImprovementMetric,
  DifferentialMetric,
  HighLowCreditRiskLevelMetric,
  PositiveNegativeCreditRiskLevelMetric,
} from '../services/dtos/riskProducts/creditRiskLevels.dto';
import { WIDGET_ID, WidgetMetricDto } from '../services/dtos/riskProducts/widgetMetric.dto';

export const FONT_FAMILY =
  'system-ui, -apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, Ubuntu, "Helvetica Neue"';

const COLORS_MAPPING = {
  [HighLowCreditRiskLevelMetric.VERY_HIGH]: colors['cat-high100'],
  [HighLowCreditRiskLevelMetric.HIGH]: colors['cat-high50'],
  [HighLowCreditRiskLevelMetric.MODERATE]: colors['cat-moderate'],
  [HighLowCreditRiskLevelMetric.LOW]: colors['cat-low50'],
  [HighLowCreditRiskLevelMetric.VERY_LOW]: colors['cat-low100'],
  [HighLowCreditRiskLevelMetric.NOT_AVAILABLE]: colors['not-available'],
  [PositiveNegativeCreditRiskLevelMetric.POSITIVE]: colors['cat-low100'],
  [PositiveNegativeCreditRiskLevelMetric.STABLE]: colors['cat-moderate'],
  [PositiveNegativeCreditRiskLevelMetric.NEGATIVE]: colors['cat-high100'],
  [PositiveNegativeCreditRiskLevelMetric.NOT_AVAILABLE]: colors['not-available'],
  [DeteriorationImprovementMetric.STRONG_DETERIORATION]: colors['cat-high100'],
  [DeteriorationImprovementMetric.MODERATE_DETERIORATION]: colors['cat-high50'],
  [DeteriorationImprovementMetric.STABLE]: colors['cat-moderate'],
  [DeteriorationImprovementMetric.MODERATE_IMPROVEMENT]: colors['cat-low50'],
  [DeteriorationImprovementMetric.STRONG_IMPROVEMENT]: colors['cat-low100'],
  [DifferentialMetric.HIGH_NEGATIVE_DIFFERENTIAL]: colors['cat-high100'],
  [DifferentialMetric.MODERATE_NEGATIVE_DIFFERENTIAL]: colors['cat-high50'],
  [DifferentialMetric.NEUTRAL_DIFFERENTIAL]: colors['cat-moderate'],
  [DifferentialMetric.MODERATE_POSITIVE_DIFFERENTIAL]: colors['cat-low50'],
  [DifferentialMetric.HIGH_POSITIVE_DIFFERENTIAL]: colors['cat-low100'],
  Neutral: colors['cat-moderate'],
  Medium: colors['cat-moderate'],
};

export const ACR_COLORS = [
  colors.cat100,
  colors.cat200,
  colors.cat300,
  colors.cat400,
  colors.cat500,
  colors.cat600,
  colors.cat700,
  colors.cat800,
];
export const getColor = (key: string): string => COLORS_MAPPING[key];

export const getTooltipMarkerHtml = (color: string): string =>
  `<span style="display: inline-block; margin-right: 4px; width: 8px; height: 8px; border-radius: 2px; background-color:${color};"></span>`;

const WIDGET_PADDINGS = 18 * 2;
export const calculateBarChartAxisLabelWidth = (itemCount: number, widgetWidth: number): number | undefined => {
  if (itemCount === 0 || widgetWidth === 0) {
    return undefined;
  }
  return Math.ceil((widgetWidth - WIDGET_PADDINGS) / itemCount);
};

export const getBarChartBaseConfig = (): BarChartOption => {
  return {
    tooltip: {
      confine: true,
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      backgroundColor: colors.tertiary1000,
      borderColor: colors.tertiary1000,
      textStyle: {
        color: colors['white-color'],
        fontSize: 12,
        fontWeight: 400,
        fontStyle: 'normal',
        fontFamily: FONT_FAMILY,
        lineHeight: 12,
        height: 28,
      },
    },
    legend: {
      show: true,
    },
    yAxis: [
      {
        type: 'value',
        minInterval: 1,
      },
    ],
    grid: {
      left: '2%',
      right: '2%',
      bottom: '2%',
      containLabel: true,
    },
  };
};

export const downloadPng = (chartInstance: ECharts, fileName: string): void => {
  const url = chartInstance.getDataURL({
    pixelRatio: 2,
    backgroundColor: colors['white-color'],
  });
  const anchorLink = document.createElement('a');
  anchorLink.download = `${fileName}.png`;
  anchorLink.target = '_blank';
  anchorLink.href = url;
  anchorLink.click();
};

const getMetricText = (widgetMetrics, selectedMetric, widgetId?) => {
  return widgetId && widgetMetrics
    ? `${widgetMetrics![widgetId].filter((i) => i.id === selectedMetric)[0]?.name} `
    : '';
};

export const getChartSubHeader = (
  widgetMetrics: Record<WIDGET_ID, WidgetMetricDto[]> | undefined,
  selectedMetric: string | undefined,
  seriesName: string,
  value: number,
  widgetId?: WIDGET_ID
): string => {
  return `${getMetricText(widgetMetrics, selectedMetric, widgetId)}${seriesName}: ${value} Companies`;
};

export const fontConfig = {
  fontSize: 12,
  fontWeight: 500,
  fontFamily: FONT_FAMILY,
  lineHeight: 12,
};

export const getTimeSeriesBaseConfig = (): LineChartOption => {
  return {
    grid: {
      left: '2',
      right: '10',
      bottom: '30',
      containLabel: true,
    },
    legend: {
      show: true,
      textStyle: {
        ...fontConfig,
        fontStyle: 'normal',
        color: colors.tertiary1000,
      },
      selectedMode: false,
      icon: 'rect',
      itemHeight: 2,
      itemWidth: 30,
    },
    tooltip: {
      trigger: 'axis',
      padding: 6,
      position(pos, params, dom, rect, size) {
        const tooltipHalfSize = size.contentSize[0] / 2;

        return [pos[0] - tooltipHalfSize, pos[1] - size.contentSize[1] - 20];
      },
      axisPointer: {
        lineStyle: {
          type: 'solid',
          width: 2,
          color: colors.tertiary700,
        },
      },
      backgroundColor: colors.tertiary1000,
      borderColor: colors.tertiary1000,
      textStyle: {
        fontSize: 11,
        fontFamily: FONT_FAMILY,
        lineHeight: 11,
        fontStyle: 'normal',
        color: colors['white-color'],
        fontWeight: 400,
        height: 28,
      },
    },
    xAxis: [
      {
        type: 'category',
        axisTick: { show: false },
        axisLabel: {
          ...fontConfig,
          fontStyle: 'normal',
          color: colors.tertiary700,
          align: 'right',
          interval: 130,
        },
        axisLine: {
          onZero: false,
          show: false,
        },
        offset: 30,
      },
    ],
  };
};
